"use client"

import { useState } from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useEditorStore } from "@/lib/editor-store"

const AUTO_ACTIONS = [
  { id: "autoHighlight", label: "Auto Highlight" },
  { id: "autoScroll", label: "Auto Scroll" },
  { id: "autoCompile", label: "Auto Compile" },
  { id: "autoSave", label: "Auto Save" },
] as const

type AutoActionId = (typeof AUTO_ACTIONS)[number]["id"]

function NeumorphicSwitch({
  checked,
  onCheckedChange,
  label,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onCheckedChange(!checked)}
      onPointerDown={(e) => e.preventDefault()}
      className={cn(
        "flex flex-col items-center justify-center gap-1.5 min-w-[108px] px-3 py-2 rounded-md",
        "transition-colors duration-150 outline-none",
        checked && "bg-[var(--ed-toggle-on-bg)]",
      )}
    >
      <span
        className={cn(
          "relative inline-flex h-[18px] w-[40px] shrink-0 items-center rounded-full",
          "bg-[var(--ed-toggle-track-bg)]",
          "shadow-[inset_0_1px_3px_rgba(0,0,0,0.14)]",
          "border border-[var(--ed-toggle-track-border)]",
        )}
      >
        <span
          className={cn(
            "absolute top-1/2 -translate-y-1/2 h-[14px] w-[18px] rounded-full",
            "bg-[var(--ed-toggle-knob-bg)]",
            "shadow-[0_1px_2px_rgba(0,0,0,0.18)]",
            "border-2 transition-all duration-150 ease-out",
            checked
              ? "left-[20px] border-[var(--ed-toggle-knob-border-on)]"
              : "left-[2px] border-[var(--ed-toggle-knob-border-off)] opacity-80",
          )}
        />
      </span>
      <span
        className={cn(
          "text-[11px] leading-tight whitespace-nowrap font-normal tracking-tight",
          checked
            ? "text-[var(--ed-toggle-label-on)]"
            : "text-[var(--ed-toggle-label-off)]",
        )}
      >
        {label}
      </span>
    </button>
  )
}

export function AutoActionsMenu() {
  const { state, setAutoCompile } = useEditorStore()
  const [autoHighlight, setAutoHighlight] = useState(true)
  const [autoScroll, setAutoScroll] = useState(true)
  const [autoSave, setAutoSave] = useState(false)

  const values: Record<AutoActionId, boolean> = {
    autoHighlight,
    autoScroll,
    autoCompile: state.autoCompile,
    autoSave,
  }

  const setValue = (id: AutoActionId, checked: boolean) => {
    switch (id) {
      case "autoHighlight":
        setAutoHighlight(checked)
        break
      case "autoScroll":
        setAutoScroll(checked)
        break
      case "autoCompile":
        setAutoCompile(checked)
        break
      case "autoSave":
        setAutoSave(checked)
        break
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-[22px] items-center gap-1 rounded-[4px] px-2.5",
            "border border-[var(--ed-ribbon-field-border)] bg-[var(--ed-ribbon-field-bg)]",
            "text-xs font-normal text-[var(--ed-ribbon-text)]",
            "hover:bg-[var(--ed-ribbon-field-hover)]",
            "outline-none",
          )}
        >
          Auto Actions
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className={cn(
          "p-0.5 min-w-0",
          "bg-[var(--ed-dropdown-trigger-bg)]",
          "border-[var(--ed-dropdown-trigger-border)]",
          "shadow-[0_2px_5px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.05)]",
        )}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-stretch">
          {AUTO_ACTIONS.map((action) => (
            <NeumorphicSwitch
              key={action.id}
              label={action.label}
              checked={values[action.id]}
              onCheckedChange={(checked) => setValue(action.id, checked)}
            />
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
